import * as api from "./client.js";

// Service types
export type ServiceInstance = {
  instanceId: string;
  version: string;
  status: "healthy" | "degraded" | "unhealthy" | "unreachable";
  url: string;
  lastHeartbeat: string;
  firstHeartbeat: string;
};

export type ServiceRoute = {
  method: string;
  path: string;
  permissions: string[];
};

export type ServiceFlagSummary = {
  key: string;
  name: string;
  flagType: string;
  scopes: string[];
};

export type ServiceNotificationAction = {
  action: string;
  description?: string;
  schema?: Record<string, unknown>;
};

export type ServiceNotificationStream = {
  stream: string;
  description?: string;
  actions: ServiceNotificationAction[];
};

export type ServiceEntry = {
  name: string;
  instances: ServiceInstance[];
  routes: ServiceRoute[];
  featureFlags: ServiceFlagSummary[];
  notifications: ServiceNotificationStream[];
  lastUpdated: string;
  firstHeartbeat: string | null;
};

export type ServicesResponse = {
  services: ServiceEntry[];
  requestId: string;
};

// Feature flag types
export type FeatureFlagVariant = {
  key: string;
  value: boolean | string | number | object;
  name?: string;
};

export type WeightedVariant = {
  variantKey: string;
  weight: number;
};

export type Rollout = {
  stickiness: string;
  seed: number;
  weightedVariants: WeightedVariant[];
};

export type TargetingOperator = "in" | "not_in";

export type TargetingRule = {
  attribute: string;
  operator: TargetingOperator;
  values: string[];
  variantKey: string;
};

export type Targeting = {
  rules: TargetingRule[];
  fallbackVariantKey: string | null;
};

export type FeatureFlagOverride = {
  id: string;
  enabled: boolean;
  rollout: Rollout | null;
  targeting: Targeting | null;
  createdAt: string;
  updatedAt: string;
};

export type FeatureFlag = {
  key: string;
  name: string | null;
  description: string | null;
  flagType: string | null;
  scopes: string[];
  declaredBy: string[];
  status: "active" | "stale";
  /**
   * When true, the flag is locked in code and its override cannot be changed
   * via the API (platform-api returns 409 for PUT/DELETE). Undefined is
   * treated as false.
   */
  locked?: boolean;
  variants: FeatureFlagVariant[];
  defaultVariantKey: string | null;
  defaults: {
    enabled: boolean;
    rollout: Rollout | null;
  } | null;
  override: FeatureFlagOverride | null;
  effective: {
    enabled: boolean;
    rollout: Rollout | null;
    targeting: Targeting | null;
  } | null;
};

export type FeatureFlagsResponse = {
  featureFlags: FeatureFlag[];
  requestId: string;
};

export type UpdateFeatureFlagConfig = {
  enabled: boolean;
  rollout?: Rollout | null;
  /** Omitted keeps the stored rules; null (or empty rules) clears them. */
  targeting?: Targeting | null;
};

/** Audit rows written before targeting existed have no `targeting` key. */
export type FeatureFlagHistoryValue = {
  enabled: boolean;
  rollout: Rollout | null;
  targeting?: Targeting | null;
};

export type FeatureFlagHistoryEntry = {
  id: number;
  occurredAt: string;
  flagKey: string;
  changeType: "create" | "update" | "delete";
  previousValue: FeatureFlagHistoryValue | null;
  newValue: FeatureFlagHistoryValue | null;
  actorId: string;
  actorEmail: string | null;
  requestId: string | null;
};

export type FeatureFlagHistoryResponse = {
  items: FeatureFlagHistoryEntry[];
  nextCursor: string | null;
  requestId: string;
};

export type FeatureFlagOverrideResponse = {
  override: {
    id: string;
    key: string;
    enabled: boolean;
    rollout: Rollout | null;
    targeting: Targeting | null;
    createdAt: string;
    updatedAt: string;
  };
  requestId: string;
};

export type NotificationActionCapability = {
  action: string;
  description?: string;
  schema?: Record<string, unknown>;
};

export type NotificationStreamCapability = {
  stream: string;
  description?: string;
  actions: NotificationActionCapability[];
  declaredBy: string[];
};

export type NotificationServiceCapabilities = {
  service: string;
  streams: NotificationStreamCapability[];
};

export type NotificationSubscriberEntry = {
  service: string;
  subscriptions: Array<{
    stream: string;
    actions: string[];
  }>;
};

export type NotificationCapabilitiesResponse = {
  services: NotificationServiceCapabilities[];
  subscribers: NotificationSubscriberEntry[];
  requestId: string;
};

export type NotificationStreamInfo = {
  type: string;
  length: number;
  firstEntryId: string | null;
  lastEntryId: string | null;
  consumerGroups: number;
};

export type NotificationStreamsResponse = {
  streams: NotificationStreamInfo[];
  requestId: string;
};

export type NotificationStreamEvent = {
  id: string;
  type: string;
  source: string;
  subject?: string;
  occurredAt: string;
  payload: unknown;
  metadata?: Record<string, string>;
};

export type NotificationStreamEntry = {
  id: string;
  timestamp: number;
  event: NotificationStreamEvent | null;
};

export type NotificationStreamEntriesResponse = {
  entries: NotificationStreamEntry[];
  requestId: string;
};

export type NotificationStreamConsumer = {
  name: string;
  pending: number;
  idle: number;
};

export type NotificationStreamConsumerGroup = {
  name: string;
  consumers: NotificationStreamConsumer[];
  pending: number;
  lastDeliveredId: string;
};

export type NotificationStreamConsumersResponse = {
  groups: NotificationStreamConsumerGroup[];
  requestId: string;
};

export type NotificationStreamSearchResponse = {
  entries: NotificationStreamEntry[];
  totalScanned: number;
  requestId: string;
};

export type MultiStreamEntriesResponse = {
  entries: NotificationStreamEntry[];
  streamTypes: string[];
  requestId: string;
};

export type MultiStreamSearchResponse = {
  entries: NotificationStreamEntry[];
  totalScanned: number;
  streamTypes: string[];
  requestId: string;
};

export type LoadTestAccount = {
  email: string;
  websiteUserId: string;
  lastLoginAt: string | null;
};

export type LoadTestPool = {
  tag: string;
  size: number;
  accounts: LoadTestAccount[];
  createdAt: string;
  lastLoginAt: string | null;
};

export type LoadTestPoolsResponse = {
  pools: LoadTestPool[];
};

export type CreateLoadTestPoolInput = {
  tag: string;
  size: number;
  domain?: string;
};

export type CreateLoadTestPoolResponse = {
  tag: string;
  accounts: { email: string; websiteUserId: string }[];
};

export type LoadTestLoginCodeResponse = {
  code: string;
  expiresAt: string;
  expiresInSeconds?: number;
};

export type TearDownLoadTestPoolResponse = {
  tag: string;
  anonymised: number;
  ssoCleanupDelivered: number;
  ssoCleanupPending: number;
};

const LOAD_TEST_ACCOUNTS_PATH = "/v1/platform/loadtest-accounts";

export const platformService = {
  async getServices(): Promise<ServicesResponse> {
    return api.get<ServicesResponse>("/v1/platform/services");
  },

  async getFeatureFlags(filters?: {
    scope?: string;
    status?: string;
  }): Promise<FeatureFlagsResponse> {
    const params = new URLSearchParams();
    if (filters?.scope) params.append("scope", filters.scope);
    if (filters?.status) params.append("status", filters.status);
    const query = params.toString() ? `?${params.toString()}` : "";
    return api.get<FeatureFlagsResponse>(`/v1/platform/feature-flags${query}`);
  },

  async updateFeatureFlag(
    key: string,
    config: UpdateFeatureFlagConfig,
  ): Promise<FeatureFlagOverrideResponse> {
    return api.put<FeatureFlagOverrideResponse>(`/v1/platform/feature-flags/${key}`, config);
  },

  async deleteFeatureFlag(key: string): Promise<void> {
    return api.del(`/v1/platform/feature-flags/${key}`);
  },

  async getFeatureFlagHistory(params?: {
    key?: string;
    limit?: number;
    before?: string;
  }): Promise<FeatureFlagHistoryResponse> {
    const search = new URLSearchParams();
    if (params?.key) search.append("key", params.key);
    if (params?.limit) search.append("limit", String(params.limit));
    if (params?.before) search.append("before", params.before);
    const query = search.toString() ? `?${search.toString()}` : "";
    return api.get<FeatureFlagHistoryResponse>(`/v1/platform/feature-flags/history${query}`);
  },

  async getNotificationCapabilities(): Promise<NotificationCapabilitiesResponse> {
    return api.get<NotificationCapabilitiesResponse>("/v1/platform/notifications/capabilities");
  },

  async getNotificationStreams(): Promise<NotificationStreamsResponse> {
    return api.get<NotificationStreamsResponse>("/v1/platform/notifications/streams");
  },

  async getNotificationStreamEntries(
    type: string,
    options?: { count?: number; before?: string },
  ): Promise<NotificationStreamEntriesResponse> {
    const params = new URLSearchParams();
    if (options?.count) params.append("count", String(options.count));
    if (options?.before) params.append("before", options.before);
    const query = params.toString() ? `?${params.toString()}` : "";
    return api.get<NotificationStreamEntriesResponse>(
      `/v1/platform/notifications/streams/${encodeURIComponent(type)}/entries${query}`,
    );
  },

  async getNotificationStreamConsumers(type: string): Promise<NotificationStreamConsumersResponse> {
    return api.get<NotificationStreamConsumersResponse>(
      `/v1/platform/notifications/streams/${encodeURIComponent(type)}/consumers`,
    );
  },

  async searchNotificationStreamEntries(
    type: string,
    query: string,
    options?: { limit?: number },
  ): Promise<NotificationStreamSearchResponse> {
    const params = new URLSearchParams();
    params.append("q", query);
    if (options?.limit) params.append("limit", String(options.limit));
    return api.get<NotificationStreamSearchResponse>(
      `/v1/platform/notifications/streams/${encodeURIComponent(type)}/search?${params.toString()}`,
    );
  },

  async getMultiStreamEntries(
    filter: string,
    options?: { count?: number },
  ): Promise<MultiStreamEntriesResponse> {
    const params = new URLSearchParams();
    params.append("filter", filter);
    if (options?.count) params.append("count", String(options.count));
    return api.get<MultiStreamEntriesResponse>(
      `/v1/platform/notifications/entries?${params.toString()}`,
    );
  },

  async searchMultiStream(
    filter: string,
    query: string,
    options?: { limit?: number },
  ): Promise<MultiStreamSearchResponse> {
    const params = new URLSearchParams();
    params.append("filter", filter);
    params.append("q", query);
    if (options?.limit) params.append("limit", String(options.limit));
    return api.get<MultiStreamSearchResponse>(
      `/v1/platform/notifications/search?${params.toString()}`,
    );
  },

  getServicesStreamUrl(): string {
    return api.url("/v1/platform/services/stream");
  },

  getFeatureFlagsStreamUrl(): string {
    return api.url("/v1/platform/feature-flags/stream");
  },

  loadTestAccounts: {
    list(): Promise<LoadTestPoolsResponse> {
      return api.get<LoadTestPoolsResponse>(LOAD_TEST_ACCOUNTS_PATH);
    },

    create(input: CreateLoadTestPoolInput): Promise<CreateLoadTestPoolResponse> {
      return api.post<CreateLoadTestPoolResponse>(LOAD_TEST_ACCOUNTS_PATH, input);
    },

    loginCode(email: string): Promise<LoadTestLoginCodeResponse> {
      return api.post<LoadTestLoginCodeResponse>(
        `${LOAD_TEST_ACCOUNTS_PATH}/${encodeURIComponent(email)}/login-code`,
        {},
      );
    },

    teardown(tag: string): Promise<TearDownLoadTestPoolResponse> {
      return api.del<TearDownLoadTestPoolResponse>(
        `${LOAD_TEST_ACCOUNTS_PATH}/${encodeURIComponent(tag)}`,
      );
    },
  },
};
